/**
 * PII scrubbing for PostHog analytics on the landing page.
 *
 * Structured fields retain the existing Polish PII masks. URL properties also
 * redact encoded PII and bearer tokens before the shared before_send hook.
 */

const PII_PATTERNS: ReadonlyArray<readonly [RegExp, string]> = [
  [/\b\d{10}\b/g, '[NIP]'],
  [/\b\d{11}\b/g, '[PESEL]'],
  [/[A-Z]{2}\d{2}[\s]?\d{4}[\s]?\d{4}[\s]?\d{4}[\s]?\d{4}[\s]?\d{4}[\s]?\d{4}/gi, '[IBAN]'],
  [/[\w.-]+@[\w.-]+\.\w+/g, '[EMAIL]'],
  [/(?:\+48[-\s]?)?\b\d{3}[-\s]?\d{3}[-\s]?\d{3}\b/g, '[PHONE]'],
  [/\b\d{2}[-\s]?\d{2}[-\s]?\d{3}[-\s]?\d{2}[-\s]?\d{2}\b/g, '[PHONE]'],
];

function scrubPiiString(value: string): string {
  return PII_PATTERNS.reduce((s, [re, mask]) => s.replace(re, mask), value);
}

const URL_PROPERTIES = new Set([
  'page_url',
  'page_path',
  '$current_url',
  '$referrer',
  '$initial_current_url',
  '$initial_referrer',
  '$pathname',
  '$initial_pathname',
  '$session_entry_url',
  '$session_entry_referrer',
  '$session_entry_pathname',
]);
const OPAQUE_TOKEN_RE = /^[A-Za-z0-9_-]{20,}$/;
const KEBAB_SLUG_RE = /^[a-z0-9]+(?:-[a-z0-9]+)+$/;
const JWT_RE = /^eyJ[A-Za-z0-9_-]{4,}\.[A-Za-z0-9_-]{4,}(?:\.[A-Za-z0-9_-]*)?$/;
const SECRET_QUERY_KEY_RE =
  /^(?:code|token|access_token|refresh_token|id_token|invite|invite_code|invitation_token|password|secret|api_key)$/i;

// Relative references and decoded separators need URL context, not scalar masking.
const URL_REFERENCE_RE = /^(?:https?:\/\/|\/|\.{1,2}\/)|[?#]|(?:^|\/)zaproszenie\//i;
const URL_REFERENCE_START_RE = /^(?:https?:\/\/|\/|\.{1,2}\/)/i;

function decodeComponent(value: string, stopAtUrl = false): string {
  // Bound nested encoding; uncertain residual encoding must not expose a secret.
  value = value.replace(/\+/g, ' ');
  if (stopAtUrl && URL_REFERENCE_RE.test(value)) return value;
  for (let count = 0; count < 4; count++) {
    let decoded: string;
    try {
      decoded = decodeURIComponent(value);
    } catch {
      decoded = value.replace(/(?:%[\da-f]{2})+/gi, (part) => {
        try {
          return decodeURIComponent(part);
        } catch {
          return '[REDACTED]';
        }
      });
    }
    if (decoded === value || (stopAtUrl && URL_REFERENCE_RE.test(decoded))) return decoded;
    value = decoded;
  }
  return /%[\da-f]{2}/i.test(value) ? '[REDACTED]' : value;
}

function redactComponent(value: string, depth = 0, restorePathContent = false): string {
  const decoded = decodeComponent(value, true);
  if (URL_REFERENCE_RE.test(decoded)) {
    const sanitized = scrubUrlPii(decoded, depth + 1);
    if (!restorePathContent) return sanitized;
    // This known sanitizer result is serialized as a URI. Its content will be
    // embedded in one enclosing path component, rather than another whole URI.
    try {
      return decodeURIComponent(sanitized);
    } catch {
      return '[REDACTED]';
    }
  }
  if (JWT_RE.test(decoded)) return '[TOKEN]';
  if (OPAQUE_TOKEN_RE.test(decoded) && !KEBAB_SLUG_RE.test(decoded)) return '[TOKEN]';
  return scrubPiiString(decoded);
}

function wellFormed(value: string): string {
  return value.replace(
    /[\uD800-\uDBFF](?![\uDC00-\uDFFF])|(?<![\uD800-\uDBFF])[\uDC00-\uDFFF]/g,
    '[REDACTED]'
  );
}

function encodeComponent(value: string): string {
  return encodeURIComponent(wellFormed(value)).replace(/%5B/g, '[').replace(/%5D/g, ']');
}

function hashUrlReference(value: string): string | null {
  // Decide plain key=value context before decoding. Encoded URL separators in
  // its apparent key may instead carry a path whose encoded '=' is still data.
  const leadingKey = value.match(/^[^/?#=]*=/)?.[0];
  if (leadingKey && !/%(?:25)*(?:2f|3f|23)/i.test(leadingKey)) return null;
  const isReference = (candidate: string) =>
    URL_REFERENCE_START_RE.test(candidate) || /[/?#]/.test(candidate);
  if (isReference(value)) return value;
  const decoded = decodeComponent(value.replace(/\+/g, '%2B'), true);
  return decoded === '[REDACTED]' || isReference(decoded) ? decoded : null;
}

function scrubParameters(value: string, depth: number, hashReferences = false): string {
  return value
    .split('&')
    .map((parameter) => {
      const reference = hashReferences ? hashUrlReference(parameter) : null;
      if (reference !== null) return encodeComponent(scrubUrlPii(reference, depth + 1));
      const equals = parameter.indexOf('=');
      if (equals === -1) return encodeComponent(redactComponent(parameter, depth));
      const key = decodeComponent(parameter.slice(0, equals));
      const sanitized = SECRET_QUERY_KEY_RE.test(key)
        ? '[TOKEN]'
        : redactComponent(parameter.slice(equals + 1), depth);
      return `${encodeComponent(scrubPiiString(key))}=${encodeComponent(sanitized)}`;
    })
    .join('&');
}

function scrubHash(value: string, depth: number): string {
  // A bare relative reference may be the first item of an outer parameter bag.
  // Explicit URI starts and an earlier URI query/fragment keep '&' in that URI.
  const items = value.split('&');
  const firstReference = hashUrlReference(items[0]);
  if (
    items.length > 1 &&
    firstReference !== null &&
    !URL_REFERENCE_START_RE.test(firstReference) &&
    !/[?#]/.test(firstReference) &&
    items.slice(1).some((item) => /^[^/?#=]*=/.test(item) || hashUrlReference(item) !== null)
  ) {
    return scrubParameters(value, depth, true);
  }
  // Hash routers carry a whole URL reference, whose query must be parsed before
  // '=' can turn the route and its first secret parameter into a parameter key.
  const reference = hashUrlReference(value);
  if (reference !== null) return scrubUrlPii(reference, depth + 1);
  return scrubParameters(value, depth, true);
}

/** Protect SDK URLs as well as landing page_url, preserving editorial slugs. */
export function scrubUrlPii(value: string, depth = 0): string {
  if (depth > 4) return '[REDACTED]';
  const hashAt = value.indexOf('#');
  const beforeHash = hashAt === -1 ? value : value.slice(0, hashAt);
  const hash = hashAt === -1 ? '' : value.slice(hashAt + 1);
  const queryAt = beforeHash.indexOf('?');
  const base = queryAt === -1 ? beforeHash : beforeHash.slice(0, queryAt);
  const query = queryAt === -1 ? '' : beforeHash.slice(queryAt + 1);
  const withoutCredentials = base.replace(
    /^((?:[a-z][a-z\d+.-]*:)?\/\/)[^/?#]*@/i,
    '$1[REDACTED]@'
  );
  const authority = withoutCredentials.match(/^((?:[a-z][a-z\d+.-]*:)?\/\/[^/?#]*)/i)?.[0] ?? '';
  const parts = withoutCredentials.slice(authority.length).split('/');
  const encodedPath = parts
    .map((part, index) => {
      // This actual invite route carries capability tokens, including UUIDs/slugs.
      if (index > 0 && decodeComponent(parts[index - 1]) === 'zaproszenie' && part)
        return '[TOKEN]';
      // '+' is a literal in paths; form-style decoding belongs to query/hash only.
      const sanitized = redactComponent(part.replace(/\+/g, '%2B'), depth, true);
      // Decoded path delimiters must remain data, after checking their contents.
      return encodeComponent(sanitized).replace(/%2B/g, '+');
    })
    .join('/');
  const encodedAuthority = encodeURI(wellFormed(scrubPiiString(authority)))
    .replace(/%5B/g, '[')
    .replace(/%5D/g, ']');
  const encodedBase = `${encodedAuthority}${encodedPath}`;
  return `${encodedBase}${queryAt === -1 ? '' : `?${scrubParameters(query, depth)}`}${hashAt === -1 ? '' : `#${scrubHash(hash, depth)}`}`;
}

export function scrubPii(value: unknown): unknown {
  if (typeof value === 'string') return scrubPiiString(value);
  if (Array.isArray(value)) return value.map(scrubPii);
  if (typeof value === 'object' && value !== null) {
    const out: Record<string, unknown> = {};
    for (const [k, v] of Object.entries(value)) {
      out[k] = URL_PROPERTIES.has(k) && typeof v === 'string' ? scrubUrlPii(v) : scrubPii(v);
    }
    return out;
  }
  return value;
}
